<template>
  <div class="py-10 text-left">
    <header>
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 class="text-3xl font-bold leading-tight text-gray-900">
          Enter Your Loan Offers
        </h1>
        <p class="mt-4 max-w-3xl text-xl leading-7 text-gray-500">
          By sharing your quote, you are helping other students with finding
          their best options. Your quotes will be shown anonymously.
        </p>
      </div>
    </header>

    <main>
      <div class="max-w-7xl mx-auto sm:px-6 lg:px-8">
        <div class="px-4 py-4 sm:px-0">
          <div class="bg-white py-5 sm:rounded-lg sm:p-6">
            <div class="md:grid md:grid-cols-3 md:gap-6">
              <div class="md:col-span-1">
                <h3 class="text-lg font-medium leading-6 text-gray-900">
                  Your information
                </h3>
                <p class="text-sm text-gray-400">
                  Emails help us confirm data authenticity, and are completely optional.
                  We don’t sell your address to anyone.
                </p>
              </div>
              <div class="mt-5 md:mt-0 md:col-span-2 lg:col-span-1">
                <form action="#" method="POST">
                  <div class="lg:flex">
                    <div class="w-full">
                      <TextInput
                        name="email"
                        label="Email (optional)"
                        type="email"
                        v-model="$email"
                        :rules="[
                          v => v.length === 0 || emailRegex.test(v) || 'Please enter a valid email.'
                        ]"
                        v-on:validation:Success="error = false"
                        v-on:validation:Error="error = true"
                      />
                    </div>
                  </div>
                  <div class="lg:flex">
                    <div class="w-full mb-5">
                      <label
                        for="creditScore"
                        class="block text-sm font-medium leading-5 text-gray-700 mb-1"
                      >
                        Loan Type
                      </label>
                      <multiselect
                        id="loanType"
                        v-model="$loanType"
                        placeholder="Loan Types"
                        :options="['Undergraduate', 'Graduate']"
                      ></multiselect>
                    </div>
                  </div>
                  <div class="lg:flex">
                    <div class="w-full mb-5">
                      <label
                        for="creditScore"
                        class="block text-sm font-medium leading-5 text-gray-700 mb-1"
                      >
                        Credit Score
                      </label>
                      <multiselect
                        id="creditScore"
                        v-model="$creditScore"
                        track-by="id"
                        label="score"
                        placeholder="Credit Score"
                        :options="reversedCreditScores"
                      ></multiselect>
                    </div>
                  </div>
                  <div class="lg:flex">
                    <div class="w-full mb-5">
                      <label
                        for="cosignerCreditScore"
                        class="block text-sm font-medium leading-5 text-gray-700 mb-1"
                      >
                        Co-signer's Credit Score
                      </label>
                      <multiselect
                        id="cosignerCreditScore"
                        v-model="$cosignerCreditScore"
                        track-by="score"
                        label="score"
                        placeholder="Co-Signer Credit Score"
                        :options="reversedCosignerCreditScores"
                      ></multiselect>
                    </div>
                  </div>
                  <div class="lg:flex">
                    <div class="w-full mb-5">
                      <label
                        for="university"
                        class="block text-sm font-medium leading-5 text-gray-700 mb-1"
                      >
                        University
                      </label>
                      <multiselect
                        id="university"
                        v-model="$university"
                        track-by="name"
                        label="name"
                        placeholder="University"
                        :options="universities"
                      ></multiselect>
                    </div>
                  </div>
                  <div class="lg:flex">
                    <div class="w-full mb-5">
                      <label
                        for="degree"
                        class="block text-sm font-medium leading-5 text-gray-700 mb-1"
                      >
                        Degree
                      </label>
                      <multiselect
                        id="degree"
                        v-model="$degree"
                        track-by="name"
                        label="name"
                        placeholder="Degree"
                        :disabled="loanType == null"
                        :options="availableDegrees"
                      ></multiselect>
                      <transition name="fade-fast">
                        <p
                          v-if="loanType == null"
                          class="mt-2 text-sm text-gray-500"
                        >
                          Please select a loan type before selecting a degree.
                        </p>
                      </transition>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div
            v-for="(quote, index) in quotes"
            :key="`${index}_quote_row`"
            class="mt-6 bg-white px-4 py-5 sm:rounded-lg sm:p-6"
          >
            <div class="md:grid md:grid-cols-3 md:gap-6">
              <div class="md:col-span-1">
                <h3 class="text-lg font-medium leading-6 text-gray-900">
                  Quote #{{ index + 1 }}
                </h3>
              </div>
              <div class="mt-5 md:mt-0 md:col-span-2 lg:col-span-1">
                <form action="#" method="POST">
                  <div class="lg:flex">
                    <div class="w-full mb-5">
                      <label
                        for="lender"
                        class="block text-sm font-medium leading-5 text-gray-700 mb-1"
                      >
                        Lender
                      </label>
                      <multiselect
                        id="lender"
                        track-by="id"
                        label="name"
                        v-model="quote.lender"
                        placeholder="Select a Lender"
                        :multiple="false"
                        :options="lenders"
                      ></multiselect>
                    </div>
                  </div>

                  <div class="lg:flex">
                    <TextInput
                      name="loan_term"
                      label="Loan Amount ($)"
                      type="number"
                      v-model="quote.loanAmount"
                      prepend="$"
                      :rules="[
                        v => v > 0 || 'Please input a number above 0.'
                      ]"
                      v-on:validation:Success="error = false"
                      v-on:validation:Error="error = true"
                    />
                  </div>

                  <div class="lg:flex">
                    <div class="w-full mb-5">
                      <label
                        for="rateType"
                        class="block text-sm font-medium leading-5 text-gray-700 mb-1"
                      >
                        Rate Type
                      </label>
                      <multiselect
                        id="rateType"
                        track-by="id"
                        label="type"
                        v-model="quote.rateType"
                        placeholder="Select Fixed or Variable"
                        :multiple="false"
                        :options="rateTypes"
                      ></multiselect>
                    </div>
                  </div>

                  <div class="lg:flex">
                    <TextInput
                      name="loan_term"
                      label="Loan Term (in years)"
                      type="number"
                      append="Years"
                      v-model="quote.loanTerm"
                      :rules="[
                        v => v > 0 || 'Please input a number above 0.'
                      ]"
                      v-on:validation:Success="error = false"
                      v-on:validation:Error="error = true"
                    />
                  </div>

                  <div class="lg:flex">
                    <div class="w-full mb-5">
                      <label
                        for="repaymentPlan"
                        class="block text-sm font-medium leading-5 text-gray-700 mb-1"
                      >
                        Repayment Plan
                      </label>
                      <multiselect
                        id="repaymentPlan"
                        track-by="id"
                        label="plan"
                        v-model="quote.repaymentPlan"
                        placeholder="Select a Repayment Plan"
                        :multiple="false"
                        :options="repaymentPlans"
                      ></multiselect>
                    </div>
                  </div>

                  <div class="lg:flex">
                    <TextInput
                      name="apr"
                      label="APR (%)"
                      hint="Include AutoPay discount if applicable."
                      type="number"
                      append="%"
                      v-model="quote.apr"
                      :rules="[
                        v => v > 0 || 'Please input a number above 0.'
                      ]"
                      v-on:validation:Success="error = false"
                      v-on:validation:Error="error = true"
                    />
                  </div>

                  <div class="lg:flex mb-0">
                    <div class="w-full flex flex-col items-start ">
                      <p class="block text-sm font-medium leading-5 text-gray-700 mb-1">
                        Was this quote sourced through LeverEdge?
                      </p>
                      <div class="flex flex-row items-start justify-center py-2">
                        <div class="flex flex-row justify-center mr-5">
                          <input
                            :id="`${index}_sourced_false`"
                            :name="`${index}_sourced`"
                            v-model="quote.sourcedViaLeverEdge"
                            type="radio"
                            :value="false"
                            class="form-radio h-4 w-4 text-green-500
                        transition duration-150 ease-in-out"
                          >
                          <label
                            :for="`${index}_sourced_false`"
                            class="ml-2 block text-sm leading-5 text-gray-900 select-none"
                          >
                            No
                          </label>
                        </div>
                        <div class="flex flex-row justify-center">
                          <input
                            :id="`${index}_sourced_true`"
                            :name="`${index}_sourced`"
                            v-model="quote.sourcedViaLeverEdge"
                            type="radio"
                            :value="true"
                            class="form-radio h-4 w-4 text-green-500
                        transition duration-150 ease-in-out"
                          >
                          <label
                            :for="`${index}_sourced_true`"
                            class="ml-2 block text-sm leading-5 text-gray-900 select-none"
                          >
                            Yes
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
                <FileInput
                  v-model="quote.screenshot"
                  label="Attach a screenshot of your loan quote (Optional)"
                  hint="Please feel free to remove any personal information like name,
                    email, etc.."
                />
                <div v-if="quotes.length > 1">
                  <button
                    @click.prevent="removeRow(index)"
                    class="w-full font-bold py-2 px-4 text-sm text-red-400 outline-none
                    focus:outline-none border-none rounded mt-6"
                  >
                    Remove
                  </button>
                </div>
                <div class="mt-6" v-if="index === quotes.length - 1">
                  <button
                    @click.prevent="addNewRow"
                    class="w-full font-bold text-green-500 border border-green-500 py-2 px-4
                    rounded outline-none focus:outline-none "
                  >
                    + Add Another Quote
                  </button>
                </div>
              </div>
            </div>
          </div>

          <button
            @click="submitForm"
            class="w-full font-semibold py-2 px-4 rounded mt-6"
            v-bind:class="[
              formValid
                ? 'bg-green-800 hover:bg-green-500 text-white'
                : 'bg-gray-200 text-gray-400'
            ]"
            :disabled="!formValid"
          >
            Submit Quotes Anonymously
          </button>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import { setSubmission, uploadFileForQuotes } from '@/services/api';
import { EmailRegex } from '@/assets/js/constants';

export default {
  data() {
    return {
      error: false,
      emailRegex: EmailRegex,
      loanType: null,
    };
  },
  computed: {
    ...mapGetters('base', [
      'reversedCreditScores',
      'reversedCosignerCreditScores',
    ]),
    ...mapState('submission', [
      'creditScore',
      'cosignerCreditScore',
      'university',
      'degree',
      'email',
      'quotes',
    ]),
    ...mapState('base', [
      'submissions',
      'universities',
      'creditScores',
      'cosignerCreditScores',
      'degrees',
      'lenders',
      'repaymentPlans',
      'rateTypes',
    ]),
    $loanType: {
      get() {
        return this.loanType
          ? `${this.loanType.charAt(0).toUpperCase()}${this.loanType.slice(1)}`
          : null;
      },
      set(value) {
        this.loanType = value ? value.toLowerCase() : value;
        this.setFormField({ field: 'degree', value: null });
      },
    },
    availableDegrees() {
      if (this.loanType == null) {
        return [];
      }
      return this.degrees.filter((degree) => degree.type === this.loanType);
    },
    $creditScore: {
      get() {
        return this.creditScore;
      },
      set(value) {
        this.setFormField({ field: 'creditScore', value });
      },
    },
    $cosignerCreditScore: {
      get() {
        return this.cosignerCreditScore;
      },
      set(value) {
        this.setFormField({ field: 'cosignerCreditScore', value });
      },
    },
    $university: {
      get() {
        return this.university;
      },
      set(value) {
        this.setFormField({ field: 'university', value });
      },
    },
    $degree: {
      get() {
        return this.degree;
      },
      set(value) {
        this.setFormField({ field: 'degree', value });
      },
    },
    $email: {
      get() {
        return this.email;
      },
      set(value) {
        this.setFormField({ field: 'email', value });
      },
    },
    quotesWithScreenshots() {
      return this.quotes.filter((quote) => !!quote.screenshot);
    },
    formValid() {
      const formValues = [
        this.$degree,
        this.$university,
        this.$creditScore,
        this.$cosignerCreditScore,
      ];
      const validQuotes = this.quotes
        .filter((quote) => Object.values(quote)
          .filter((value) => value != null && String(value).length > 0)
          .length === Object.keys(quote).length);
      return !this.error
        && validQuotes.length === this.quotes.length
        && formValues.filter((value) => !!value).length === formValues.length;
    },
  },
  methods: {
    ...mapActions('submission', [
      'setFormField',
      'pushQuote',
      'removeQuote',
      'setQuoteValue',
      'resetState',
    ]),
    updateQuotesField(index, value, key) {
      this.setQuoteValue({ index, value, key });
    },
    removeRow(index) {
      this.removeQuote(index);
    },
    addNewRow(quote) {
      this.pushQuote(quote);
    },
    submitForm() {
      const quotes = this.quotes.map((quote) => ({
        lenderId: quote.lender.id,
        rateTypeId: quote.rateType.id,
        repaymentPlanId: quote.repaymentPlan.id,
        apr: quote.apr,
        loanTerm: quote.loanTerm,
        loanAmount: quote.loanAmount,
        sourcedViaLeverEdge: quote.sourcedViaLeverEdge,
        verified: quote.verified,
        borrowed: quote.borrowed,
      }));

      setSubmission({
        creditScore: this.$creditScore,
        cosignerCreditScore: this.$cosignerCreditScore,
        university: this.$university,
        degree: this.$degree,
        email: this.$email,
      }, quotes)
        .then((response) => {
          if (response) {
            if (this.quotesWithScreenshots.length) {
              if (response.data && response.data.data && response.data.data.id) {
                const mappedQuotes = this.quotesWithScreenshots.map((quote) => ({
                  ...quote,
                  id: this.getQuoteId(quote, response.data.data.quotes) || 0,
                  file: quote.screenshot,
                }));
                uploadFileForQuotes(response.data.data.id, mappedQuotes);
              }
            }

            this.$router.push({ name: 'browse.quotes', params: { success: 'true' } });
            this.resetState();
          }
        });
    },
    getQuoteId(quoteToMap, quotes) {
      const match = quotes.find((quote) => Number(quote.apr) === Number(quoteToMap.apr)
        && Number(quote.loanAmount) === Number(quoteToMap.loanAmount)
        && Number(quote.loanTerm) === Number(quoteToMap.loanTerm)
        && quote.lender.id === quoteToMap.lender.id
        && quote.rateType.id === quoteToMap.rateType.id
        && quote.repaymentPlan.id === quoteToMap.repaymentPlan.id);
      return match ? match.id : null;
    },
  },
};
</script>
